import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PG } from './theme';

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.92); }
  50% { opacity: 1; transform: scale(1.08); }
`;

const FENCE_PATH = /```[^\n]*\b(?:path|file|filename)\s*=\s*["']?([^\s"'`]+)/g;

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Which file the reply is writing now, and how many it has finished. */
function fileProgress(text: string): { current: string | null; done: number } {
  const starts = Array.from(text.matchAll(FENCE_PATH));
  if (!starts.length) return { current: null, done: 0 };
  const last = starts[starts.length - 1];
  const after = text.slice((last.index ?? 0) + last[0].length);
  const closed = /\n```/.test(after);
  return { current: closed ? null : last[1], done: closed ? starts.length : starts.length - 1 };
}

interface Props {
  startedAt: number;
  thinking: string;
  streamingText: string;
  isBuilder: boolean;
}

export const WorkingIndicator: React.FC<Props> = ({ startedAt, thinking, streamingText, isBuilder }) => {
  const [now, setNow] = useState(Date.now());
  const [showThinking, setShowThinking] = useState(false);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const elapsed = now - startedAt;
  const progress = useMemo(() => (isBuilder ? fileProgress(streamingText) : { current: null, done: 0 }), [isBuilder, streamingText]);
  const thinkingTail = useMemo(() => {
    const lines = thinking.trim().split('\n').filter(Boolean);
    return lines.slice(-3).join('\n');
  }, [thinking]);

  let label: string;
  if (progress.current) label = `Writing ${progress.current}`;
  else if (streamingText) label = isBuilder && progress.done ? 'Writing notes' : 'Writing the reply';
  else if (thinking) label = isBuilder ? 'Planning the app' : 'Thinking';
  else if (elapsed < 8000) label = 'Starting';
  else label = isBuilder ? 'Planning the app' : 'Thinking';

  const longWait = !streamingText && elapsed > 90_000;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(56,189,248,0.45)',
        bgcolor: 'rgba(56,189,248,0.08)',
        overflow: 'hidden',
      }}
    >
      <LinearProgress
        sx={{
          height: 3,
          bgcolor: 'rgba(56,189,248,0.15)',
          '& .MuiLinearProgress-bar': { bgcolor: PG.accent },
        }}
      />
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <AutoAwesomeIcon sx={{ fontSize: 18, color: PG.accent, animation: `${pulse} 1.6s ease-in-out infinite` }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: PG.text }}>
            {label}…
          </Typography>
          {isBuilder && progress.done > 0 && (
            <Typography variant="caption" sx={{ color: PG.muted }}>
              {progress.done} file{progress.done === 1 ? '' : 's'} done
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: PG.muted, fontVariantNumeric: 'tabular-nums' }}>
            {formatElapsed(elapsed)}
          </Typography>
        </Stack>

        {thinkingTail && !streamingText && (
          <Box sx={{ mt: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setShowThinking((v) => !v)}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: PG.muted,
                fontSize: '0.75rem',
                '&:focus-visible': { outline: `2px solid ${PG.accent}`, borderRadius: 0.5 },
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 16, transform: showThinking ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              {showThinking ? 'Hide thinking' : 'Show thinking'}
            </Box>
            <Collapse in={showThinking} collapsedSize={0}>
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  mt: 0.5,
                  mb: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontStyle: 'italic',
                  color: PG.muted,
                  maxHeight: 180,
                  overflow: 'auto',
                }}
              >
                {thinking.slice(-1500)}
              </Typography>
            </Collapse>
            {!showThinking && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.25, color: PG.muted, fontStyle: 'italic', whiteSpace: 'pre-wrap', opacity: 0.8 }}
              >
                {thinkingTail.slice(-240)}
              </Typography>
            )}
          </Box>
        )}

        {longWait && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PG.muted }}>
            Big builds can plan for a few minutes before the first file appears. It's still working — you can
            keep using the page.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
